/**
 * WorkBuddy Cloud SDK 公共类型（#89098）
 *
 * 契约权威来源（不要在此另行发明字段）：
 *  - `services/cmd/agentserver/docs/genie-baas/genie-baas-phase1-design.md` §5 / §6
 *  - `packages/workbuddy-server/src/cloud-service/types.ts`（`CloudServicePublicConfig`）
 *
 * 本文件只有类型与常量，不含任何 I/O。
 */

import type { CloudAuthStorage } from './modules/auth/types.js';

/**
 * 四个云服务模块。数据面路径按 `/.cloud/{module}/**` 组织，
 * 前缀 `/.cloud/` 是刻意选的，避免与生成应用自有路由冲突（设计文档 §5.0）。
 */
export type CloudModuleName = 'auth' | 'database' | 'storage' | 'llm';

/**
 * 创建客户端的入参，字段与 `CloudServicePublicConfig` 下发给应用的部分一一对应。
 *
 * ⚠️ 这里只接受公开配置。envId、CloudBase publish_key / api_key 只存服务端，
 * 任何响应都不下发（设计文档 §5.2），因此不要为它们加配置项。
 */
export interface WorkBuddyCloudOptions {
    /**
     * 数据面基址，例如 `https://app.workbuddy.link`。末尾斜杠会被归一化掉。
     *
     * **省略即用当前页面同源，这是推荐用法** —— 数据面 `/.cloud/**` 与应用页面本就
     * 同域，同源永远正确。显式传绝对地址反而有失效模式：它会被写进前端产物，应用
     * 重新发布 / 追加认证域名后拿到新域名，产物里的旧地址不变，请求便打到旧域名上，
     * 而服务端按 Origin 精确匹配必然拒绝（现场表现为跨域预检失败，极难归因）。
     *
     * 需要显式传的场景：本地开发页面跑在 dev server、数据面在别处；以及非浏览器
     * 运行时（无 `location.origin` 可回落，此时省略会抛配置错误）。
     */
    endpoint?: string;
    /**
     * Google OAuth Relay 的环境中心基址，例如
     * `https://test4-api.workbuddy.cn/v2/as/genie-baas/oauth`。
     *
     * 它与 endpoint 是两条入口：endpoint 是每应用发布域名，承载 `/.cloud/**`；
     * OAuth Relay 走中心 APISIX，不能根据 endpoint 拼接。
     *
     * **网页微信扫码登录必传**：`signInWithOAuth({ provider: 'wechat' })` 用它拼
     * Relay 的 `/authorize`，缺失直接返回 `requires oauthRelayBaseUrl`。Google 登录
     * 虽已下线，该字段并未随之停发——后端一直在 `publicConfig` 里返回它。
     *
     * 省略时回落为空串（小程序不走 Relay，可以不传）。
     */
    oauthRelayBaseUrl?: string;
    /**
     * 我方签发的半公开 key，格式 `wbpk_{appId}_{随机}`。
     *
     * 它只标识「哪个应用」、不携带权限，可以打进前端产物；安全性由服务端 Origin
     * 精确匹配提供（设计文档 §5.2 / §7.3）。仍不应打进日志。
     */
    publishableKey: string;
    /**
     * 自定义 fetch，便于测试与非标准运行时注入。缺省用全局 `fetch`。
     */
    fetch?: typeof globalThis.fetch;
    /**
     * 凭据存储。缺省用 localStorage（不可用时退回内存）。
     *
     * 什么时候需要显式传：
     *  - 测试里要一个**干净的、与当前登录态无关**的客户端（例如验证「未登录
     *    时写数据会被拒」——若沿用 localStorage 就会带上现有身份，测不到）
     *  - 非浏览器运行时（Node、小程序）里没有 localStorage
     *
     * ⚠️ **不要传 cookie 实现。** 兄弟子域可读写共享后缀 cookie，那是已识别
     * 的越权面（设计文档 §7.1）。这个选项存在是为了可注入，不是为了给 cookie
     * 留口子。
     */
    storage?: CloudAuthStorage;
}

/** 归一化后的运行期配置，四个模块共享同一份。 */
export interface CloudRuntimeConfig {
    /** 归一化后的 endpoint（无尾斜杠）。 */
    readonly endpoint: string;
    /** 归一化后的中心 OAuth Relay 基址（无尾斜杠）。 */
    readonly oauthRelayBaseUrl: string;
    readonly publishableKey: string;
    readonly fetch: typeof globalThis.fetch;
}

/**
 * 配置非法。**在初始化阶段就抛**，而不是等到第一次业务调用才失败 ——
 * 生成应用里这类错误几乎都是 publicConfig 没接上，早失败才好定位。
 */
export class WorkBuddyCloudConfigError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'WorkBuddyCloudConfigError';
    }
}
